import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { getSavedPrompts } from "../../actions/saved-prompts/getSavedPrompts";
import { updateSavedPrompts } from "../../actions/saved-prompts/updateSavedPrompts";
import { SavedPromptsMap } from "../../actions/saved-prompts/savedPromptsTypes";
import {
  CUSTOM_PROMPT_SLOTS,
  CustomPromptButtonConfig,
  DEFAULT_CUSTOM_PROMPTS,
  TransformStorageKey,
} from "./transformConfig";

interface CustomPromptButtonsProps {
  disabled?: boolean;
  isBusy?: boolean;
  onRunPrompt: (prompt: string, title: string) => Promise<void> | void;
}

/**
 * Reads the locally-cached prompts. Returns `null` when nothing has ever been
 * cached (caller falls back to defaults). An empty map is a VALID state — the
 * user deleted everything — and must NOT be coerced back to defaults
 * (Trello #270: deleted buttons kept resurrecting).
 */
const getStoredButtons = (): SavedPromptsMap | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(TransformStorageKey.CUSTOM_PROMPTS);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as Record<string, CustomPromptButtonConfig>;
    const buttons: SavedPromptsMap = {};
    for (const [slotKey, value] of Object.entries(parsed)) {
      const slot = Number(slotKey);
      if (
        Number.isInteger(slot) &&
        slot >= 0 &&
        slot < CUSTOM_PROMPT_SLOTS &&
        value &&
        typeof value.title === "string" &&
        typeof value.prompt === "string"
      ) {
        buttons[slot] = {
          title: value.title,
          prompt: value.prompt,
        };
      }
    }

    return buttons;
  } catch (error) {
    console.error("Failed to load custom prompts:", error);
    return null;
  }
};

const saveButtonsToLocal = (buttons: SavedPromptsMap) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      TransformStorageKey.CUSTOM_PROMPTS,
      JSON.stringify(buttons),
    );
  } catch (error) {
    console.error("Failed to save custom prompts:", error);
  }
};

const getDefaultButtons = (): SavedPromptsMap => ({ ...DEFAULT_CUSTOM_PROMPTS });

const CustomPromptButtons: React.FC<CustomPromptButtonsProps> = ({
  disabled = false,
  isBusy = false,
  onRunPrompt,
}) => {
  const [buttons, setButtons] = useState<SavedPromptsMap>({});
  const [editMode, setEditMode] = useState(false);
  const [configuringSlot, setConfiguringSlot] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("");
  const initialSnapshotRef = useRef("");
  const saveTimeoutRef = useRef<number | null>(null);
  // Tracks a scheduled-but-not-yet-fired API save so unmount can FLUSH it
  // instead of dropping it. Tab switches / remounts (new extraction, clearing
  // Paste text) used to cancel the debounced save, silently losing deletes
  // (Trello #270).
  const pendingSaveRef = useRef<SavedPromptsMap | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadButtons = async () => {
      setIsLoading(true);
      setSaveStatus("");

      try {
        const apiButtons = await getSavedPrompts();
        // null = user has never saved anything → show defaults.
        // {} = user deliberately deleted everything → respect the empty state.
        const nextButtons = apiButtons === null ? getDefaultButtons() : apiButtons;

        if (!isMounted) {
          return;
        }

        setButtons(nextButtons);
        initialSnapshotRef.current = JSON.stringify(nextButtons);
        saveButtonsToLocal(nextButtons);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        const fallbackButtons = getStoredButtons() ?? getDefaultButtons();
        setButtons(fallbackButtons);
        initialSnapshotRef.current = JSON.stringify(fallbackButtons);
        setSaveStatus("Using local saved prompts until the API is available.");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadButtons();

    return () => {
      isMounted = false;
      if (saveTimeoutRef.current !== null) {
        window.clearTimeout(saveTimeoutRef.current);
      }
      // Flush (don't drop) a pending save — otherwise a delete made just
      // before a remount never reaches the API and the button resurrects.
      if (pendingSaveRef.current !== null) {
        const toFlush = pendingSaveRef.current;
        pendingSaveRef.current = null;
        void updateSavedPrompts(toFlush).catch((error) => {
          console.error("Failed to flush pending prompt save on unmount:", error);
        });
      }
    };
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const serializedButtons = JSON.stringify(buttons);
    if (serializedButtons === initialSnapshotRef.current) {
      return;
    }

    saveButtonsToLocal(buttons);
    setSaveStatus("Saving...");

    if (saveTimeoutRef.current !== null) {
      window.clearTimeout(saveTimeoutRef.current);
    }

    pendingSaveRef.current = buttons;
    saveTimeoutRef.current = window.setTimeout(async () => {
      pendingSaveRef.current = null;
      try {
        const savedButtons = await updateSavedPrompts(buttons);
        // The API echoes what was saved — including an empty map. Do NOT
        // coerce empty back to defaults; that resurrects deleted buttons.
        initialSnapshotRef.current = JSON.stringify(savedButtons);
        setButtons(savedButtons);
        saveButtonsToLocal(savedButtons);
        setSaveStatus("Saved");
        window.setTimeout(() => setSaveStatus(""), 1800);
      } catch (error) {
        console.error("Failed to persist prompts to API:", error);
        initialSnapshotRef.current = serializedButtons;
        setSaveStatus("Saved locally. Changes will sync when the API is available.");
      }
    }, 500);
  }, [buttons, isLoading]);

  const activeConfig = useMemo(
    () => (configuringSlot !== null ? buttons[configuringSlot] : undefined),
    [buttons, configuringSlot],
  );

  useEffect(() => {
    if (configuringSlot === null) {
      setTitle("");
      setPrompt("");
      return;
    }

    setTitle(activeConfig?.title || "");
    setPrompt(activeConfig?.prompt || "");
  }, [activeConfig, configuringSlot]);

  const openConfig = (slot: number) => {
    setConfiguringSlot(slot);
  };

  const handleSlotClick = async (slot: number) => {
    const config = buttons[slot];
    if (editMode || !config) {
      openConfig(slot);
      return;
    }

    if (disabled || isBusy) {
      return;
    }

    await onRunPrompt(config.prompt, config.title);
  };

  const handleSave = () => {
    if (configuringSlot === null || !title.trim() || !prompt.trim()) {
      return;
    }

    setButtons((prev) => ({
      ...prev,
      [configuringSlot]: {
        title: title.trim(),
        prompt: prompt.trim(),
      },
    }));
    setConfiguringSlot(null);
  };

  const handleDelete = () => {
    if (configuringSlot === null) {
      return;
    }

    setButtons((prev) => {
      const next = { ...prev };
      delete next[configuringSlot];
      return next;
    });
    setConfiguringSlot(null);
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.25,
            color: "rgba(226, 232, 240, 0.72)",
          }}
        >
          <CircularProgress size={14} />
          <Typography sx={{ fontSize: "0.74rem" }}>Loading saved prompts...</Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: configuringSlot !== null ? 1.5 : 0 }}>
        {Array.from({ length: CUSTOM_PROMPT_SLOTS }, (_, slot) => {
          const config = buttons[slot];
          return (
            <Button
              key={slot}
              title={config?.title || "Add saved prompt"}
              variant="outlined"
              size="small"
              onClick={() => void handleSlotClick(slot)}
              disabled={disabled || isBusy || isLoading}
              sx={{
                flex: "1 1 110px",
                minWidth: 0,
                fontSize: "0.78rem",
                py: 0.75,
                borderColor: "rgba(148, 163, 184, 0.18)",
                color: "rgba(226, 232, 240, 0.88)",
                backgroundColor: config ? "rgba(30, 41, 59, 0.62)" : "rgba(15, 23, 42, 0.38)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                justifyContent: "flex-start",
              }}
            >
              {config?.title || "+"}
            </Button>
          );
        })}
        <Button
          variant={editMode ? "contained" : "outlined"}
          size="small"
          onClick={() => setEditMode((prev) => !prev)}
          disabled={isLoading}
          sx={{
            minWidth: 90,
            fontSize: "0.78rem",
            py: 0.75,
          }}
        >
          {editMode ? "Done" : "✏️"}
        </Button>
      </Box>

      {!!saveStatus && (
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(148, 163, 184, 0.95)", mb: configuringSlot !== null ? 1 : 0 }}>
          {saveStatus}
        </Typography>
      )}

      {configuringSlot !== null && (
        <Box
          sx={{
            p: 2,
            borderRadius: "16px",
            backgroundColor: "rgba(8, 15, 24, 0.72)",
            border: "1px solid rgba(148, 163, 184, 0.16)",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--t4)",
              mb: 1,
            }}
          >
            {activeConfig ? "Edit saved prompt" : "Add saved prompt"}
          </Typography>

          <Box sx={{ display: "grid", gap: 1.25 }}>
            <TextField
              size="small"
              label="Button title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              multiline
              minRows={4}
              label="Prompt"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              inputProps={{ maxLength: 5000 }}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button variant="contained" onClick={handleSave} disabled={!title.trim() || !prompt.trim()}>
                Save
              </Button>
              <Button variant="outlined" onClick={() => setConfiguringSlot(null)}>
                Cancel
              </Button>
              {activeConfig && (
                <Button color="error" variant="outlined" onClick={handleDelete}>
                  Delete
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CustomPromptButtons;
